import { defineComponent, reactive } from '@vue/composition-api';
import { TableHeader, OnColumns, ConfigIconCoordinate } from '../../shared/type';

/**
 * リアクティブな変数の型定義
 */
export type reactiveData = {
    showConfigMenu: boolean;                     // 列設定メニューの表示切替
    configIconCoordinate: ConfigIconCoordinate;  // 列設定アイコンの座標
    onColumns: OnColumns[];                      // 列設定項目
    displayTableHeaders: TableHeader[];          // 画面に表示するテーブルヘッダー情報
    showWarningDialog: boolean;                  // 列設定アイコンの座標がnullだった際に表示するダイアログの表示切替
}

/**
 * 引数
 */
interface Props {
    initialDisplayTableHeaders: TableHeader[],     // 画面に初期表示するテーブルヘッダー情報
    tableHeaders: TableHeader[],                   // テーブルヘッダー情報(全項目)
    onColumns: OnColumns[]                         // 列設定情報
}

/**
 * 列設定メニューコンポーネント
 */
export default defineComponent({
    props: {
        initialDisplayTableHeaders: Array,
        tableHeaders: Array,
        onColumns: Array
    },
    setup(props: Props, context) {

        // リアクティブな変数の宣言
        const state: reactiveData = reactive<reactiveData>({
            showConfigMenu: false,
            configIconCoordinate: { x: null, y: null },
            onColumns: [],
            displayTableHeaders: [],
            showWarningDialog: false,
        });
        // 全て表示のチェックボックスの前回の値
        let pastAll = false;

        // 親コンポーネントから引数データを取得
        state.onColumns = props.onColumns;
        const tableHeaders = props.tableHeaders;
        const initialDisplayTableHeaders = props.initialDisplayTableHeaders;
        state.displayTableHeaders = initialDisplayTableHeaders;
        
        /**
         * 列設定アイコンがクリックされた際に発火する
         * 列設定アイコンの座標を取得し、メニューを表示する
         * 
         * @param configIconCoordinate 列設定アイコンの座標
         */
        function showMenu(configIconCoordinate: any) {
            // 列設定アイコンの座標を取得
            state.configIconCoordinate = configIconCoordinate;

            // 受け取った座標のどちらかがnullだった場合、ダイアログを表示
            if (state.configIconCoordinate.x === null || state.configIconCoordinate.y === null) {
                state.showWarningDialog = true;

                return;
            } else {
                // 座標取得
                state.configIconCoordinate.x = configIconCoordinate.x.getBoundingClientRect().left;
                state.configIconCoordinate.y = configIconCoordinate.y.getBoundingClientRect().bottom;
            }

            // 列設定メニュー表示切替
            state.showConfigMenu = !state.showConfigMenu;
        }

        /**
         * 列設定メニューの項目がクリックされた際に発火する
         * テーブルヘッダー情報を切り替える
         */
        function setColumn() {
            // ヘッダー情報初期化
            state.displayTableHeaders = [];

            // ヘッダー配列数取得
            const tableLength = tableHeaders.length;

            // すべてを表示フラグ初期化
            let isAllSelect = false;
            let index = 0;
            let allCheckCnt = 0;

            for (const table of tableHeaders) {
                for (const column of state.onColumns) {
                    if (state.onColumns[0].value === pastAll) {
                        // 全て表示に変化がなかった場合
                        if (column.value === true && table.text === column.text) {
                            // 選択されたカラムを、ヘッダー情報に追加
                            state.displayTableHeaders.push(table);
                            allCheckCnt++;
                        }
                    } else {
                        // 全て表示に変化があった場合
                        if (column.value === true && column.text === '全て表示') {
                            // 全て表示にチェックが入っていた場合
                            for (const allCol of tableHeaders) {
                                // 全チェックボックスにチェックを入れる
                                state.onColumns[index].value = true;

                                // 全カラムを表示状態にする
                                state.displayTableHeaders.push(allCol);
                                index++;
                            }
                            pastAll = true;
                        } else {
                            resetDisplaySetting();
                            pastAll = false;
                        }

                        isAllSelect = true;
                        break;
                    }
                }
                if (isAllSelect) {
                    break;
                }
            }

            // '全て表示'以外のすべてのボタンにチェックが入った場合は'全て表示'にチェックを入れる
            if (!isAllSelect) {
                if (state.onColumns.length - 1 === allCheckCnt) {
                    state.onColumns[0].value = true;
                    pastAll = true;
                } else {
                    state.onColumns[0].value = false;
                    pastAll = false;
                }

                // 更新・削除ボタンカラムを最後に追加
                state.displayTableHeaders.push(tableHeaders[tableLength - 1]);
            }
            context.emit('getTableHeader', state.displayTableHeaders);
        }

        /**
         * 列設定のチェックをする
         */
        function resetDisplaySetting() {
            let index = 0;

            // 表示カラムを初期状態にする
            state.displayTableHeaders = initialDisplayTableHeaders;

            // 全チェックボックスのチェックを外す
            for (index; tableHeaders.length > index; index++) {
                state.onColumns[index].value = false;
            }

            // 初期表示対象のカラムにだけチェックを入れる
            for (const initialCol of state.displayTableHeaders) {
                index = 0;
                for (const col of state.onColumns) {
                    // 初期表示対象のカラムだった場合
                    if (initialCol.text === col.text) {
                        state.onColumns[index].value = true;
                        break;
                    }
                    index++;
                }
            }
        }

        return {
            state,
            showMenu,
            setColumn,
        };
    }
});
